// The low-poly sheet under the Automate composer, and the tiles the explore
// state is assembled from. Pure markup builders: the controller owns the state
// and hands a plain model in.
//
// Two jobs:
//   recognition — in "know" mode, whatever the prompt names lights up and the
//     rest dims, so it is obvious which file / tab / header Automate heard.
//   assembly    — in "explore" mode, the same graphic is the drag source, and
//     its parts become tiles in the step chain.
//
// Silhouettes carry meaning and are worth keeping: a tab is a folder tab, a
// header is a pill, a flow word is a chevron, a time word is a capsule.

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => ESCAPES[char]);
}

// Tile silhouettes. Sizes are one notch up from the design file to match the
// larger type this page already uses.
const TILE_BASE =
  'h-[30px] inline-flex items-center px-[10px] border font-mono text-[13.5px] leading-none whitespace-nowrap transition-all';

// Parts of the sheet speak in the confirmation accent (--automate-accent, set
// by the picker in the header); the vocabulary tiles keep their own colours,
// which are what tells a flow word from a time word.
const ACCENT_TILE = 'border-[var(--automate-accent-edge)] bg-[var(--automate-accent-soft)] text-[var(--automate-accent-text)]';
const ACCENT_TILE_FAINT =
  'border-[var(--automate-accent-edge)] bg-[var(--automate-accent-faint)] text-[var(--automate-accent-text)]';

export const TILE_KINDS = {
  file: `rounded-[6px] ${ACCENT_TILE}`,
  tab: `rounded-t-[8px] rounded-b-none ${ACCENT_TILE}`,
  header: `rounded-full ${ACCENT_TILE}`,
  row: `rounded-[4px] ${ACCENT_TILE_FAINT}`,
  cell: `rounded-[3px] ${ACCENT_TILE_FAINT}`,
  test: 'rounded-[4px] font-semibold border-[#475569] bg-[rgba(71,85,105,0.22)] text-[#cbd5e1]',
  // Typed prose, so it is set in the UI face rather than mono.
  text: 'rounded-[6px] border-[#334155] bg-[rgba(2,6,23,0.6)] text-[#e2e8f0] font-sans text-[14px]',
  value: 'rounded-[5px] border-dashed border-[#64748b] bg-[rgba(2,6,23,0.6)] text-[#e2e8f0]',
  action: 'rounded-[7px] font-semibold border-[#334155] bg-[rgba(30,41,59,0.55)] text-[#e2e8f0]',
  flow: 'rounded-l-[2px] rounded-r-[12px] border-[#a78bfa] bg-[rgba(167,139,250,0.16)] text-[#ddd6fe]',
  time: 'rounded-full border-[#fbbf24] bg-[rgba(251,191,36,0.14)] text-[#fde68a]',
  app: 'rounded-[7px] border-[#334155] bg-[rgba(30,41,59,0.55)] text-[#e2e8f0]'
};

export function tileClass(kind, { grab = false } = {}) {
  const shape = TILE_KINDS[kind] || TILE_KINDS.action;
  return `${TILE_BASE} ${shape} ${grab ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`;
}

// A named part of the sheet, in one of three recognition states:
//   on      — the prompt names it
//   dim     — something else is named, this is not
//   neutral — the prompt names nothing yet, so nothing is singled out
function recognitionClass(state, excluded) {
  if (excluded) return 'opacity-70 border-dashed line-through';
  if (state === 'on') return 'shadow-[0_0_0_3px_var(--automate-accent-ring)] border-[var(--automate-accent)]';
  if (state === 'dim') return 'opacity-55';
  return '';
}

function partButton(part, { mode, kind }) {
  const grab = mode === 'explore';
  const classes = `${tileClass(kind, { grab })} ${recognitionClass(part.state, part.excluded)}`;
  // What a click writes into the prompt. "Tab: Orders" and "Column: Total (C)"
  // rather than the bare word, so the name still reads as that part of the
  // sheet once it is sitting in a sentence.
  const token = part.token || part.label;
  const title = part.excluded
    ? `${part.label} — Automate is not reading this. Right-click to put it back.`
    : mode === 'explore'
      ? `Add ${token} to this step`
      : `${part.state === 'on' ? 'Remove' : 'Add'} “${token}”${
          kind === 'header' ? '' : ' — right-click to stop Automate reading it'
        }`;

  return `
    <button type="button"
            ${grab ? 'draggable="true"' : ''}
            data-automate-target="sheetPart"
            data-action="click->automate#toggleSheetPart contextmenu->automate#toggleSheetPartExcluded dragstart->automate#tileDragStart dragend->automate#tileDragEnd"
            data-part-kind="${kind}"
            data-part-label="${escapeHtml(part.label)}"
            data-part-token="${escapeHtml(token)}"
            data-part-key="${escapeHtml(part.key || part.label)}"
            aria-pressed="${part.state === 'on'}"
            title="${escapeHtml(title)}"
            class="${classes}">${escapeHtml(part.label)}</button>`;
}

// model: { mode, file: part, headers: [part], tabs: [part], rows: [part],
//          cellRows: [[part]], tabHint }
export function sheetGraphicHtml(model) {
  const { mode } = model;
  const file = model.file || { label: 'Spreadsheet', state: 'neutral' };
  const fileDim = file.state === 'dim' ? 'opacity-55' : '';
  const fileStrike = file.excluded ? 'line-through opacity-70' : '';

  const headers = (model.headers || [])
    .map((header) => partButton(header, { mode, kind: 'header' }))
    .join('');
  const tabs = (model.tabs || []).map((tab) => partButton(tab, { mode, kind: 'tab' })).join('');

  const rows =
    mode === 'explore'
      ? `
        <span class="flex-none flex flex-col gap-[3px]">
          ${(model.rows || []).map((row) => partButton(row, { mode, kind: 'row' })).join('')}
        </span>`
      : '';

  const cells =
    mode === 'explore'
      ? `
        <span class="flex-1 min-w-0 flex flex-col gap-[3px]">
          ${(model.cellRows || [])
            .map(
              (cellRow) =>
                `<span class="flex gap-[3px]">${cellRow
                  .map((cell) => partButton(cell, { mode, kind: 'cell' }))
                  .join('')}</span>`
            )
            .join('')}
        </span>`
      : '';

  const gridRow = mode === 'explore' ? `<div class="flex gap-2 px-3 pb-3 pt-2">${rows}${cells}</div>` : '';

  return `
    <div class="rounded-[11px] border border-[#1f2937] bg-[rgba(11,18,32,0.72)] overflow-hidden">
      <div class="flex items-center gap-2 px-3 pt-2.5 pb-2 ${mode === 'explore' ? 'border-b border-[#1f2937]' : ''}">
        <button type="button"
                ${mode === 'explore' ? 'draggable="true"' : ''}
                data-automate-target="sheetPart"
                data-action="click->automate#toggleSheetPart contextmenu->automate#toggleSheetPartExcluded dragstart->automate#tileDragStart dragend->automate#tileDragEnd"
                data-part-kind="file"
                data-part-label="${escapeHtml(file.label)}"
                data-part-key="file"
                aria-pressed="${file.state === 'on'}"
                title="${escapeHtml(
                  file.excluded
                    ? `${file.label} — Automate is not reading this file. Right-click to put it back.`
                    : `${file.state === 'on' ? 'Remove' : 'Add'} “${file.label}” — right-click to stop Automate reading this file`
                )}"
                class="inline-flex items-center gap-2 border-0 bg-transparent p-0 font-sans text-[15px] font-semibold text-[var(--automate-accent-text)] ${
                  mode === 'explore' ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
                } ${fileDim} ${fileStrike}">
          <span class="w-3 h-3 rounded-[3px] bg-[var(--automate-accent)] shrink-0"></span>${escapeHtml(file.label)}
        </button>
        <span class="ml-auto font-mono text-[12px] text-[#475569]">${escapeHtml(model.tabHint || '')}</span>
      </div>
      ${headers ? `<div class="flex flex-wrap gap-1.5 px-3 pb-3 ${mode === 'explore' ? 'pt-2.5' : ''}">${headers}</div>` : ''}
      ${gridRow}
      ${
        tabs
          ? `<div class="flex gap-1 px-3 border-t border-[#1f2937] bg-[rgba(2,6,23,0.45)] overflow-x-auto">${tabs}</div>`
          : ''
      }
    </div>`;
}

// The vocabulary of a Sheets automation, made touchable. Tokens are plain
// English on purpose: everything the builder produces is prompt text, and no
// generated code is ever shown here.
export const BUILDER_GROUPS = [
  { name: 'tests', kind: 'test', tiles: ['is', 'is not', '>', '<', 'contains', 'is empty', 'is checked', 'changed'] },
  { name: 'values', kind: 'value', tiles: ['Pending', '0', 'today', 'TRUE', 'https://', 'name@company.com'] },
  {
    name: 'do',
    kind: 'action',
    tiles: ['read', 'write', 'add a row', 'delete a row', 'clear', 'check the box', 'sort', 'colour it']
  },
  { name: 'flow', kind: 'flow', tiles: ['if', 'else', 'for each row', 'wait 5 min', 'stop'] },
  { name: 'when', kind: 'time', tiles: ['on edit', 'on form', 'on open', 'every Monday', '9am'] }
];

export function builderGroupsHtml() {
  return BUILDER_GROUPS.map(
    (group) => `
      <div class="border border-[#1f2937] rounded-[10px] bg-[rgba(11,18,32,0.5)] px-3 pt-2 pb-2.5">
        <div class="font-mono text-[11px] tracking-[0.12em] uppercase text-[#475569]">${escapeHtml(group.name)}</div>
        <div class="mt-2 flex flex-wrap items-end gap-1.5">
          ${group.tiles
            .map(
              (label) => `
              <button type="button"
                      draggable="true"
                      data-action="click->automate#addBuilderTile dragstart->automate#tileDragStart dragend->automate#tileDragEnd"
                      data-part-kind="${group.kind}"
                      data-part-label="${escapeHtml(label)}"
                      title="Add ${escapeHtml(label)} to this step"
                      class="${tileClass(group.kind, { grab: true })}">${escapeHtml(label)}</button>`
            )
            .join('')}
        </div>
      </div>`
  ).join('');
}

// Values are placeholders — "Pending", "0", "name@company.com" say what kind of
// thing goes there, not what yours is — so once one is in the chain it is an
// input you can type your own value into. Typed text stays editable for the
// same reason.
export const EDITABLE_KINDS = ['value', 'text'];

function tileBodyHtml(tile, stepIndex, tileIndex) {
  const label = escapeHtml(tile.label);
  if (!EDITABLE_KINDS.includes(tile.kind)) {
    return `<span class="${tileClass(tile.kind)} cursor-default" title="${escapeHtml(
      tile.token || tile.label
    )}">${label}</span>`;
  }

  return `
    <input type="text"
           data-automate-target="builderTileInput"
           data-action="input->automate#editBuilderTile keydown->automate#builderTileKeydown"
           data-step-index="${stepIndex}"
           data-tile-index="${tileIndex}"
           value="${label}"
           size="${Math.max(String(tile.label || '').length, 3)}"
           aria-label="Edit ${label}"
           class="${tileClass(tile.kind)} cursor-text min-w-[44px] focus:outline-none focus:border-[var(--automate-accent)] focus:text-[#f8fafc]">`;
}

// One tile inside a step, with its own remove button. The × is a real target,
// not a dot: it is the only way back out of a step.
function stepTileHtml(tile, stepIndex, tileIndex) {
  return `
    <span class="inline-flex items-center gap-0.5">
      ${tileBodyHtml(tile, stepIndex, tileIndex)}
      <button type="button"
              data-action="click->automate#removeBuilderTile"
              data-step-index="${stepIndex}"
              data-tile-index="${tileIndex}"
              aria-label="Remove ${escapeHtml(tile.label)}"
              title="Remove ${escapeHtml(tile.label)}"
              class="h-[26px] w-[26px] inline-flex items-center justify-center rounded-md border-0 bg-transparent text-[19px] leading-none text-[#64748b] hover:text-[#fca5a5] hover:bg-[rgba(127,29,29,0.25)] cursor-pointer">×</button>
    </span>`;
}

// model: { steps: [[tile]], current: [tile], dragging: Boolean }
export function builderChainHtml(model) {
  const dropZone = model.dragging
    ? 'border-[var(--automate-accent)] bg-[var(--automate-accent-faint)]'
    : 'border-[#1f2937]';

  // A short spine between boxes, so a chain of steps reads as one sequence
  // rather than as stacked cards. Sits at the step-number gutter.
  const connector = `
    <span class="h-3 w-px ml-[7px] bg-[var(--automate-accent-edge)] shrink-0" aria-hidden="true"></span>`;

  const steps = (model.steps || [])
    .map(
      (tiles, index) => `
        <div class="flex gap-2.5 items-start border border-[#1f2937] rounded-[10px] bg-[rgba(2,6,23,0.6)] px-3 py-2.5"
             data-automate-target="builderStep"
             data-step-index="${index}"
             data-action="dragover->automate#tileDragOver drop->automate#tileDropOnStep">
          <span class="font-mono text-[13px] text-[#475569] pt-[7px]">${index + 1}</span>
          <span class="flex-1 min-w-0 flex flex-wrap items-center gap-2">
            ${tiles.map((tile, tileIndex) => stepTileHtml(tile, index, tileIndex)).join('')}
          </span>
          <button type="button"
                  data-action="click->automate#removeBuilderStep"
                  data-step-index="${index}"
                  aria-label="Remove step ${index + 1}"
                  title="Remove step ${index + 1}"
                  class="h-[30px] w-[30px] shrink-0 inline-flex items-center justify-center rounded-md border-0 bg-transparent text-[21px] leading-none text-[#64748b] hover:text-[#fca5a5] hover:bg-[rgba(127,29,29,0.25)] cursor-pointer">×</button>
        </div>`
    )
    .join(connector);

  const tiles = (model.current || []).map((tile, tileIndex) => stepTileHtml(tile, -1, tileIndex)).join('');

  // The open box is a tag input: tiles, then a caret you can type in. Backspace
  // or Delete on an empty input takes the last tile back out, the way removing
  // a word does.
  const text = model.text || '';
  const placeholder = (model.current || []).length
    ? 'or type it'
    : 'click, drag or type what should happen';

  return `
    <div class="flex flex-col gap-2.5">
      ${steps}
      ${steps ? connector : ''}
      <div class="flex flex-wrap items-center gap-2 min-h-[54px] border border-dashed rounded-[10px] px-3 py-2.5 transition-colors cursor-text ${dropZone}"
           data-automate-target="builderOpenBox"
           data-action="click->automate#focusBuilderText dragover->automate#tileDragOver drop->automate#tileDropOnCurrent">
        ${tiles}
        <input type="text"
               data-automate-target="builderText"
               data-action="input->automate#updateBuilderText keydown->automate#builderTextKeydown"
               value="${escapeHtml(text)}"
               placeholder="${escapeHtml(placeholder)}"
               aria-label="Type this step"
               class="flex-1 min-w-[140px] h-[30px] border-0 bg-transparent p-0 font-sans text-[15px] text-[#f1f5f9] placeholder:text-[#475569] focus:outline-none">
      </div>
    </div>`;
}
